/* eslint-disable no-console */
import { useEffect, useRef, useState, type FormEvent, useMemo } from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { Send } from 'lucide-react';
import amplifyOutputs from '../../../amplify_outputs.json';
import { client } from '../../lib/AmplifyClient';

const SEND_EMAIL_FUNCTION_URL =
  (amplifyOutputs as any)?.custom?.sendEmailApiUrl ||
  (window as any).__SEND_EMAIL_API_URL__ ||
  '';

const GENERATE_INVITE_FUNCTION_URL =
  (amplifyOutputs as any)?.custom?.generateInviteApiUrl ||
  (window as any).__GENERATE_INVITE_API_URL__ ||
  '';

const isValidEmail = (s?: string | null) =>
  !!s && /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(s.trim());

const nowIso = () => new Date().toISOString();

// layout constants
const BASE_TOP_GAP_PX = 32;
const BANNER_TOP_SPACER = 20;

export default function InitiatorComponent() {
  const navigate = useNavigate();
  const location = useLocation();

  // Auth gate
  const [authChecked, setAuthChecked] = useState(false);

  // Identity
  const [initiatorFirstName, setInitiatorFirstName] = useState('');
  const [initiatorLastName, setInitiatorLastName] = useState('');
  const [initiatorEmail, setInitiatorEmail] = useState('');

  // Answers
  const [issueInput, setIssueInput] = useState('');
  const [desiredInput, setDesiredInput] = useState('');
  const [whyInput, setWhyInput] = useState('');
  const [collabFirst, setCollabFirst] = useState('');
  const [collabLast, setCollabLast] = useState('');
  const [collabEmail, setCollabEmail] = useState('');

  // Session
  const [issueId, setIssueId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<string>(''); // For UI feedback
  const navigatedRef = useRef(false);
  const creatingRef = useRef(false);

  // UI
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  /** Require auth before any data calls */
  useEffect(() => {
    (async () => {
      try {
        const user = await getCurrentUser();
        if (!user) throw new Error('No user');
        const attrs = await fetchUserAttributes();
        setInitiatorFirstName(attrs.given_name || '');
        setInitiatorLastName(attrs.family_name || '');
        setInitiatorEmail(attrs.email || '');
        setAuthChecked(true);
        setLoading(false);
      } catch {
        const next = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?next=${next}`, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Polling for generated questions */
  useEffect(() => {
    if (!issueId || !authChecked || navigatedRef.current) return;
    let cancelled = false;

    async function checkQuestions() {
      if (!issueId || cancelled || navigatedRef.current) return;
      try {
        const res = await (client as any).models.Issue.get(
          { id: issueId },
          { authMode: 'userPool' }
        );
        const rec: any = (res as any)?.data ?? res;
        if (!rec || typeof rec !== 'object') return;

        const arr = Array.isArray(rec.inferredQuestionsInitiator)
          ? rec.inferredQuestionsInitiator
          : [];

        if (!cancelled && arr.length > 0 && !navigatedRef.current) {
          navigatedRef.current = true;
          navigate(`/session/${encodeURIComponent(issueId)}/questions`, {
            replace: true,
          });
        }
      } catch (err: any) {
        if (!cancelled && String(err?.name).includes('NoValidAuthTokens')) {
          const next = encodeURIComponent(location.pathname + location.search);
          navigate(`/login?next=${next}`, { replace: true });
        }
      }
    }

    checkQuestions();
    const intervalId = setInterval(checkQuestions, 5000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [issueId, authChecked, navigate, location.pathname, location.search]);

  /** Steps */
  const steps = useMemo(
    () => [
      {
        key: 'issue',
        label:
          'What is your view of the issue you’d like to address in this session? Please provide some details.',
        placeholder: 'Share your view…',
        type: 'textarea' as const,
        value: issueInput,
        set: setIssueInput,
        required: true,
      },
      {
        key: 'desired',
        label: 'What is your desired outcome for this disagreement?',
        placeholder: 'Describe your desired outcome…',
        type: 'textarea' as const,
        value: desiredInput,
        set: setDesiredInput,
        required: true,
      },
      {
        key: 'why',
        label: 'Why does solving this issue matter to you?',
        placeholder: 'Explain why this matters…',
        type: 'textarea' as const,
        value: whyInput,
        set: setWhyInput,
        required: true,
      },
      {
        key: 'collabFirst',
        label: 'Collaborator first name',
        type: 'text' as const,
        value: collabFirst,
        set: setCollabFirst,
        required: true,
      },
      {
        key: 'collabLast',
        label: 'Collaborator last name',
        type: 'text' as const,
        value: collabLast,
        set: setCollabLast,
        required: true,
      },
      {
        key: 'collabEmail',
        label: 'Collaborator email',
        placeholder: 'name@example.com',
        type: 'text' as const,
        value: collabEmail,
        set: setCollabEmail,
        required: true,
        validate: (v: string) => isValidEmail(v),
        validateMsg: 'Enter a valid email.',
      },
    ],
    [issueInput, desiredInput, whyInput, collabFirst, collabLast, collabEmail]
  );

  const [currentStep, setCurrentStep] = useState(0);
  const isLast = currentStep === steps.length - 1;
  const stepNo = currentStep + 1;
  const total = steps.length;
  const pct = Math.round((stepNo / total) * 100);

  useEffect(() => {
    inputRef.current?.focus();
  }, [currentStep]);

  // NEW: clear any previous error when switching steps
  useEffect(() => {
    setError(null);
  }, [currentStep]);

  // helper to handle field changes and clear error when user types valid-ish input
  function handleFieldChange(stepIndex: number, raw: string) {
    const s = steps[stepIndex];
    s.set(raw);

    if (!error) return;

    const val = raw.trim();
    if (s.required && !val) return;
    if ((s as any).validate && !(s as any).validate(val)) return;

    // if we reach here, the current value is non-empty (and valid, if a validator exists)
    setError(null);
  }

  function handleNext() {
    setError(null);
    const s = steps[currentStep];
    const val = (s.value || '').trim();
    if (s.required && !val) {
      setError('Please provide a response.');
      return;
    }
    if ((s as any).validate && !(s as any).validate(val)) {
      setError((s as any).validateMsg || 'Invalid input.');
      return;
    }
    if (!isLast) setCurrentStep(currentStep + 1);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const s = steps[currentStep];
    const val = (s.value || '').trim();
    if (s.required && !val) {
      setError('Please provide a response.');
      return;
    }
    if ((s as any).validate && !(s as any).validate(val)) {
      setError((s as any).validateMsg || 'Invalid input.');
      return;
    }

    await finalizeCreateAndInvite(collabEmail);
  }

  async function finalizeCreateAndInvite(emailArg: string) {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setSubmitting(true);
    setGenerationStatus('Drafting invitation...');

    try {
      const normalizedEmail = emailArg.trim().toLowerCase();

      // 1. Create the issue record
      const payload = {
        status: 'created',
        collaboratorJoined: false,
        inviteEmailStatus: 'pending',
        inviteEmailSentAt: null,
        lastActivityAt: nowIso(),
        initiatorEmail,
        initiatorFirstName,
        initiatorLastName,
        collaboratorFirstName: collabFirst || '',
        collaboratorLastName: collabLast || '',
        collaboratorEmail: normalizedEmail,
        initiatorIssue: [issueInput.trim()],
        desiredOutcomeInitiator: [desiredInput.trim()],
        whyOutcomeMattersInitiator: [whyInput.trim()],
      };

      const res = await (client as any).models.Issue.create(payload, { authMode: 'userPool' });
      const sessionId = (res as any)?.data?.id ?? (res as any)?.id;
      if (!sessionId) throw new Error('Failed to create session ID');
      setIssueId(sessionId);

      // 2. Generate Rephrased Invite Content
      let aiSubject = '';
      let aiBody = '';

      if (GENERATE_INVITE_FUNCTION_URL) {
        try {
          setGenerationStatus('Reframing issue with AI...');
          const genRes = await fetch(GENERATE_INVITE_FUNCTION_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              initiatorName: `${initiatorFirstName} ${initiatorLastName}`,
              collaboratorName: `${collabFirst} ${collabLast}`,
              issue: issueInput,
              whyMatters: whyInput,
              desiredOutcome: desiredInput,
            }),
          });

          if (genRes.ok) {
            const genData = await genRes.json();
            aiSubject = genData.subject;
            aiBody = genData.body;
          }
        } catch (genErr) {
          console.warn('AI generation failed', genErr);
        }
      }

      setGenerationStatus('Sending email...');

      // Replace generic placeholders with actual names
      const replaceGenericNames = (text: string) => {
        if (!text) return text;
        return text
          .replace(/\[Initiator Name\]/gi, `${initiatorFirstName} ${initiatorLastName}`)
          .replace(/\[Initiator\]/gi, initiatorFirstName)
          .replace(/\[Collaborator Name\]/gi, `${collabFirst} ${collabLast}`)
          .replace(/\[Collaborator\]/gi, collabFirst)
          .replace(/\byour collaborator\b/gi, `${collabFirst} ${collabLast}`)
          .replace(/\ba colleague\b/gi, `${initiatorFirstName} ${initiatorLastName}`);
      };

      const finalSubject = replaceGenericNames(aiSubject);
      const finalBody = replaceGenericNames(aiBody);

      const ok = await sendCollaboratorEmailDebug(sessionId, {
        initiatorFirstName,
        initiatorLastName,
        collaboratorFirstName: collabFirst,
        collaboratorLastName: collabLast,
        collaboratorEmail: normalizedEmail,
        customSubject: finalSubject,
        customBody: finalBody,
      });

      await (client as any).models.Issue.update(
        {
          id: sessionId,
          inviteEmailStatus: ok ? 'sent' : 'failed',
          inviteEmailSentAt: nowIso(),
          inviteEmailSubject: finalSubject || '',
          inviteEmailBody: finalBody || '',
        },
        { authMode: 'userPool' }
      );
    } catch (e: any) {
      setError(e?.message || 'Failed to start session.');
    } finally {
      setSubmitting(false);
      creatingRef.current = false;
      setGenerationStatus('');
    }
  }

  const isWaiting = !!issueId && !navigatedRef.current;
  const showForm = !isWaiting;

  if (!authChecked || loading) {
    return (
      <div className="gradient-calm" style={{ position: 'fixed', inset: 0, overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: BASE_TOP_GAP_PX,
          }}
        >
          <Container fluid="md" className="px-3 w-100 d-flex justify-content-center">
            <Card className="w-100 mx-auto text-center pillar-blue" style={{ maxWidth: 900 }}>
              <Card.Body className="py-5">
                <Spinner animation="border" role="status" />
                <div className="mt-3">Loading…</div>
              </Card.Body>
            </Card>
          </Container>
        </div>
      </div>
    );
  }

  const stepDef = steps[currentStep];

  return (
    <div className="gradient-calm" style={{ position: 'fixed', inset: 0, overflow: 'hidden' }}>
      {!isWaiting && <div style={{ height: BANNER_TOP_SPACER }} />}

      {isWaiting && (
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            className="sessionNotice"
            style={{
              padding: '8px 16px',
              textAlign: 'center',
              margin: '0 auto',
              maxWidth: 900,
            }}
          >
            <strong>Waiting for acceptance</strong> from{' '}
            <span className="name">
              {collabFirst || 'your collaborator'} {collabLast || ''}
            </span>
            . We’ll take you to your questions after {collabFirst} accepts the invite.
          </div>
        </div>
      )}

      {!isWaiting && (
        <div
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: BASE_TOP_GAP_PX,
            boxSizing: 'border-box',
          }}
        >
          {showForm && (
            <Container fluid="md" className="px-3 w-100 d-flex justify-content-center">
              <Card
                className="w-100 mx-auto pillar-blue"
                style={{
                  maxWidth: 900,
                  maxHeight: `calc(100vh - ${2 * BASE_TOP_GAP_PX}px)`,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Card.Header as="h3" className="fw-bold">
                  Start a Session
                </Card.Header>
                <Card.Body className="p-4" style={{ overflowY: 'auto' }}>
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="text-muted">
                        Step {stepNo} of {total}
                      </small>
                      <small className="text-muted">{pct}% complete</small>
                    </div>
                    <div className="progress" style={{ height: '6px' }}>
                      <div
                        className="progress-bar bg-primary"
                        role="progressbar"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>

                  {error && (
                    <Alert variant="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}

                  <Form
                    onSubmit={
                      isLast
                        ? handleSubmit
                        : (e) => {
                            e.preventDefault();
                            handleNext();
                          }
                    }
                  >
                    <div className="p-3 mb-3 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <p className="mb-0" style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                        {stepDef.label}
                      </p>
                    </div>

                    {stepDef.type === 'textarea' ? (
                      <Form.Control
                        as="textarea"
                        rows={5}
                        placeholder={stepDef.placeholder}
                        value={stepDef.value}
                        onChange={(e) => handleFieldChange(currentStep, e.target.value)}
                        disabled={submitting}
                        ref={inputRef as any}
                        className="shadow-sm"
                        autoFocus
                      />
                    ) : (
                      <Form.Control
                        type="text"
                        placeholder={stepDef.placeholder}
                        value={stepDef.value}
                        onChange={(e) => handleFieldChange(currentStep, e.target.value)}
                        disabled={submitting}
                        ref={inputRef as any}
                        className="shadow-sm"
                        autoFocus
                      />
                    )}

                    <div className="d-flex justify-content-end align-items-center mt-4">
                      {!isLast ? (
                        <Button type="button" onClick={handleNext} disabled={submitting}>
                          Continue →
                        </Button>
                      ) : !issueId ? (
                        <Button type="submit" variant="success" disabled={submitting}>
                          {submitting ? (
                            <>
                              <Spinner size="sm" animation="border" className="me-2" />
                              {generationStatus || 'Processing...'}
                            </>
                          ) : (
                            <>
                              <Send size={16} className="me-1" />
                              Complete & Invite ✓
                            </>
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Container>
          )}
        </div>
      )}
    </div>
  );
}

/** Email helper that now accepts Custom Subject/Body */
async function sendCollaboratorEmailDebug(
  sessionId: string,
  p: {
    initiatorFirstName: string;
    initiatorLastName: string;
    collaboratorFirstName: string;
    collaboratorLastName: string;
    collaboratorEmail: string;
    customSubject?: string;
    customBody?: string;
  }
) {
  const url = SEND_EMAIL_FUNCTION_URL;
  if (!url) return false;

  const collLink = `${window.location.origin}/coll/${encodeURIComponent(sessionId)}`;

  const subject = p.customSubject || `You're invited to an Accordia session`;

  const body = p.customBody
    ? `${p.customBody}\n\nView session: ${collLink}`
    : `Dear ${p.collaboratorFirstName 
      } has invited you to a private Accordia session.\n\nView your session:\n${collLink}\n\nAccordia Team`;

  const esc = (s: string) =>
    String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const contentHtml = p.customBody
    ? p.customBody
        .split('\n')
        .map((line) =>
          line ? `<p style="margin:0 0 16px 0;">${esc(line)}</p>` : ''
        )
        .join('')
    : `<p style="margin:0 0 16px 0;">Dear ${esc(p.collaboratorFirstName || 'Collaborator')},</p>
       <p style="margin:0 0 24px 0;">${esc(
         p.initiatorFirstName || 'A colleague'
       )} has invited you to a private Accordia session.</p>`;

  const html = `
<!doctype html>
<html>
  <body style="margin:0;padding:0;background:#f6f7f9;font-family:Arial,Helvetica,sans-serif;">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:#f6f7f9;padding:24px 0;">
      <tr>
        <td align="center">
          <table role="presentation" width="560" cellspacing="0" cellpadding="0" style="background:#ffffff;border-radius:8px;padding:24px;">
            <tr>
              <td style="font-size:16px;line-height:24px;color:#202124;">
                ${contentHtml}
                <p style="text-align:center;margin:0 0 24px 0;">
                  <a href="${collLink}"
                     style="display:inline-block;padding:12px 20px;background:#34A853;color:#ffffff;text-decoration:none;border-radius:6px;font-weight:600;">
                    View Session
                  </a>
                </p>
                <p style="margin:24px 0 0 0;color:#5f6368;font-size:12px;line-height:18px;">
                  Use the button or copy and paste this link into your browser:<br/>
                  <a href="${collLink}" style="color:#1a73e8;text-decoration:none;">${collLink}</a>
                </p>
                <p style="margin:24px 0 0 0;">Accordia Team</p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`.trim();

  try {
    const ac = new AbortController();
    const t = setTimeout(() => ac.abort(), 15000);

    const resp = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        recipient: p.collaboratorEmail,
        subject,
        body,
        html,
      }),
      signal: ac.signal,
    });
    clearTimeout(t);
    return resp.ok;
  } catch {
    return false;
  }
}
